import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from "recharts";
import { Activity, LogOut, Watch, ListChecks, FlaskConical, HeartPulse, Loader2, Plus, Moon } from "lucide-react";

const SOURCES = ["Oura Ring", "WHOOP", "Apple Watch", "Garmin", "Fitbit", "manual"];

export default function PatientPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [labs, setLabs] = useState([]);
  const [trends, setTrends] = useState({});
  const [tasks, setTasks] = useState([]);
  const [wearables, setWearables] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [trendKey, setTrendKey] = useState("");

  const loadAll = () => {
    api.get("/portal/me").then((r) => setMe(r.data));
    api.get("/portal/lab-exams").then((r) => setLabs(r.data));
    api.get("/portal/trends").then((r) => setTrends(r.data));
    api.get("/portal/tasks").then((r) => setTasks(r.data));
    api.get("/portal/wearables").then((r) => setWearables(r.data));
    api.get("/portal/symptoms").then((r) => setSymptoms(r.data));
  };
  useEffect(() => { loadAll(); }, []);

  const toggleTask = async (t) => {
    const status = t.status === "concluida" ? "pendente" : "concluida";
    await api.put(`/portal/tasks/${t.id}?status=${status}`);
    setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, status } : x)));
  };

  const trendKeys = Object.keys(trends);
  const activeKey = trendKey || trendKeys[0];
  const active = trends[activeKey];
  const chartData = (active?.points || []).map((p) => ({ date: p.date, valor: p.value }));
  const pendingTasks = tasks.filter((t) => t.status !== "concluida").length;

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-primary text-primary-foreground">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-primary-foreground/15 flex items-center justify-center">
              <Activity className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <div>
              <p className="font-heading font-extrabold leading-none">LIA <span className="text-ai">Saúde</span></p>
              <p className="text-[11px] text-primary-foreground/70 mt-0.5">Toda a sua saúde, conectada</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
            onClick={async () => { await logout(); navigate("/login"); }} data-testid="portal-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Sair
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-light tracking-tighter" data-testid="portal-greeting">
            Olá, {(me?.name || user?.name || "").split(" ")[0]}.
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {me?.doctor_name ? `Acompanhado por ${me.doctor_name}. ` : ""}
            Veja seus resultados, tarefas e conecte seus dispositivos.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card className="p-5 border-border shadow-none" data-testid="portal-stat-tasks">
            <ListChecks className="w-5 h-5 text-ai mb-2" />
            <div className="font-mono-data text-2xl font-semibold">{pendingTasks}</div>
            <div className="text-xs text-muted-foreground">Tarefas pendentes</div>
          </Card>
          <Card className="p-5 border-border shadow-none">
            <FlaskConical className="w-5 h-5 text-chart-3 mb-2" />
            <div className="font-mono-data text-2xl font-semibold">{labs.length}</div>
            <div className="text-xs text-muted-foreground">Exames</div>
          </Card>
          <Card className="p-5 border-border shadow-none">
            <Watch className="w-5 h-5 text-chart-5 mb-2" />
            <div className="font-mono-data text-2xl font-semibold">{wearables.length}</div>
            <div className="text-xs text-muted-foreground">Leituras de dispositivos</div>
          </Card>
        </div>

        <Tabs defaultValue="tarefas">
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="tarefas" data-testid="portal-tab-tasks"><ListChecks className="w-4 h-4 mr-2" /> Minhas tarefas</TabsTrigger>
            <TabsTrigger value="resultados" data-testid="portal-tab-results"><FlaskConical className="w-4 h-4 mr-2" /> Resultados</TabsTrigger>
            <TabsTrigger value="dispositivos" data-testid="portal-tab-devices"><Watch className="w-4 h-4 mr-2" /> Dispositivos</TabsTrigger>
            <TabsTrigger value="sintomas" data-testid="portal-tab-symptoms"><HeartPulse className="w-4 h-4 mr-2" /> Sintomas</TabsTrigger>
          </TabsList>

          <TabsContent value="tarefas">
            {tasks.length === 0 ? (
              <Card className="p-8 text-center text-muted-foreground border-dashed">Nenhuma tarefa no seu plano.</Card>
            ) : (
              <div className="space-y-2">
                {tasks.map((t) => (
                  <Card key={t.id} className="p-4 border-border shadow-none flex items-center gap-3" data-testid={`portal-task-${t.id}`}>
                    <Checkbox checked={t.status === "concluida"} onCheckedChange={() => toggleTask(t)} data-testid={`portal-task-check-${t.id}`} />
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${t.status === "concluida" ? "line-through text-muted-foreground" : ""}`}>{t.title}</p>
                      <p className="text-xs text-muted-foreground">{t.type}{t.due_date ? ` · prazo ${t.due_date}` : ""}</p>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="resultados">
            {trendKeys.length > 0 && (
              <Card className="p-6 border-border shadow-none mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-heading text-lg font-medium">{active?.label}</h4>
                  <Select value={activeKey} onValueChange={setTrendKey}>
                    <SelectTrigger className="w-56" data-testid="portal-trend-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{trendKeys.map((k) => <SelectItem key={k} value={k}>{trends[k].label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                    {active && <ReferenceArea y1={active.ref_min} y2={active.ref_max} fill="hsl(var(--success))" fillOpacity={0.08} />}
                    <Line type="monotone" dataKey="valor" stroke="hsl(var(--chart-2))" strokeWidth={2.5} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            )}
            {labs.length === 0 ? (
              <Card className="p-8 text-center text-muted-foreground border-dashed">Nenhum resultado disponível ainda.</Card>
            ) : (
              <div className="space-y-3">
                {labs.map((e) => (
                  <Card key={e.id} className="p-5 border-border shadow-none" data-testid={`portal-lab-${e.id}`}>
                    <div className="flex justify-between mb-3">
                      <h4 className="font-medium">{e.panel_label}</h4>
                      <span className="text-sm text-muted-foreground">{e.exam_date}</span>
                    </div>
                    <div className="space-y-1">
                      {e.measurements.map((m) => (
                        <div key={m.marker_key} className="flex items-center justify-between text-sm">
                          <span>{m.label}</span>
                          <div className="flex items-center gap-3">
                            <span className="font-mono-data">{m.value} {m.unit}</span>
                            <StatusBadge status={m.status} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="dispositivos"><DevicesPanel wearables={wearables} onAdded={loadAll} /></TabsContent>
          <TabsContent value="sintomas"><SymptomsPanel symptoms={symptoms} onAdded={loadAll} /></TabsContent>
        </Tabs>

        <p className="text-xs text-muted-foreground italic mt-8 border-t border-border pt-4">
          As informações e análises têm caráter de apoio e não substituem a avaliação do seu médico.
        </p>
      </div>
    </div>
  );
}

function DevicesPanel({ wearables, onAdded }) {
  const [f, setF] = useState({ date: new Date().toISOString().slice(0, 10), source: "Oura Ring", sleep_hours: "", resting_hr: "", hrv: "", steps: "", spo2: "", bp_sys: "", bp_dia: "", weight: "" });
  const [saving, setSaving] = useState(false);
  const num = (v) => (v === "" ? null : parseFloat(v));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/portal/wearables", {
        date: f.date, source: f.source, sleep_hours: num(f.sleep_hours), resting_hr: num(f.resting_hr),
        hrv: num(f.hrv), steps: num(f.steps), spo2: num(f.spo2), bp_sys: num(f.bp_sys), bp_dia: num(f.bp_dia), weight: num(f.weight),
      });
      toast.success("Dados enviados ao seu médico");
      setF({ ...f, sleep_hours: "", resting_hr: "", hrv: "", steps: "", spo2: "", bp_sys: "", bp_dia: "", weight: "" });
      onAdded();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 border-border shadow-none">
        <h4 className="font-heading font-medium mb-1">Conectar dados do dispositivo</h4>
        <p className="text-sm text-muted-foreground mb-4">Insira as leituras do seu Oura Ring, WHOOP, Apple Watch, etc.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="space-y-2"><Label>Data</Label><Input type="date" value={f.date} onChange={set("date")} data-testid="portal-wearable-date" /></div>
          <div className="space-y-2"><Label>Fonte</Label>
            <Select value={f.source} onValueChange={(v) => setF({ ...f, source: v })}>
              <SelectTrigger data-testid="portal-wearable-source"><SelectValue /></SelectTrigger>
              <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Sono (h)</Label><Input type="number" step="any" value={f.sleep_hours} onChange={set("sleep_hours")} data-testid="portal-wearable-sleep" /></div>
          <div className="space-y-2"><Label>FC repouso</Label><Input type="number" value={f.resting_hr} onChange={set("resting_hr")} data-testid="portal-wearable-hr" /></div>
          <div className="space-y-2"><Label>HRV (ms)</Label><Input type="number" value={f.hrv} onChange={set("hrv")} data-testid="portal-wearable-hrv" /></div>
          <div className="space-y-2"><Label>PA sistólica</Label><Input type="number" value={f.bp_sys} onChange={set("bp_sys")} data-testid="portal-wearable-bpsys" placeholder="120" /></div>
          <div className="space-y-2"><Label>PA diastólica</Label><Input type="number" value={f.bp_dia} onChange={set("bp_dia")} data-testid="portal-wearable-bpdia" placeholder="80" /></div>
          <div className="space-y-2"><Label>Peso (kg)</Label><Input type="number" step="any" value={f.weight} onChange={set("weight")} data-testid="portal-wearable-weight" placeholder="72.5" /></div>
          <div className="space-y-2"><Label>Passos</Label><Input type="number" value={f.steps} onChange={set("steps")} data-testid="portal-wearable-steps" /></div>
        </div>
        <Button className="mt-4" onClick={save} disabled={saving} data-testid="portal-wearable-save">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Enviar dados
        </Button>
      </Card>

      {wearables.length > 0 && (
        <Card className="p-6 border-border shadow-none">
          <h4 className="font-heading font-medium mb-4 flex items-center gap-2"><Moon className="w-4 h-4 text-chart-5" /> Histórico</h4>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={wearables}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <Line type="monotone" dataKey="sleep_hours" name="Sono" stroke="hsl(var(--chart-5))" strokeWidth={2.5} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="resting_hr" name="FC" stroke="hsl(var(--destructive))" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
}

function SymptomsPanel({ symptoms, onAdded }) {
  const [desc, setDesc] = useState("");
  const [sev, setSev] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!desc.trim()) { toast.error("Descreva o sintoma"); return; }
    setSaving(true);
    try {
      await api.post("/portal/symptoms", { date: new Date().toISOString().slice(0, 10), description: desc, severity: sev ? parseInt(sev) : null });
      toast.success("Sintoma registrado");
      setDesc(""); setSev(""); onAdded();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 border-border shadow-none">
        <h4 className="font-heading font-medium mb-4">Relatar sintoma</h4>
        <div className="space-y-3">
          <Textarea rows={2} placeholder="Descreva como está se sentindo..." value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="portal-symptom-desc" />
          <div className="flex gap-3 items-end">
            <div className="space-y-2 w-40">
              <Label>Intensidade (0-10)</Label>
              <Input type="number" min="0" max="10" value={sev} onChange={(e) => setSev(e.target.value)} data-testid="portal-symptom-severity" />
            </div>
            <Button onClick={save} disabled={saving} data-testid="portal-symptom-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Registrar
            </Button>
          </div>
        </div>
      </Card>

      {symptoms.length > 0 && (
        <div className="space-y-2">
          {symptoms.map((s) => (
            <Card key={s.id} className="p-4 border-border shadow-none flex items-center justify-between" data-testid={`portal-symptom-${s.id}`}>
              <div>
                <p className="text-sm">{s.description}</p>
                <p className="text-xs text-muted-foreground">{s.date}</p>
              </div>
              {s.severity != null && <Badge variant="secondary">{s.severity}/10</Badge>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
